// Note: Remove require packages in package.json if exported as external modules
import axios from 'axios';
import jwt from 'jsonwebtoken';
import { reportError, addBreadcrumb, describeRequestUrl } from '@/utils/error-reporter';

/**
 * Correlation id sent as X-Request-Id on every backend call. Reported alongside
 * the failure so a customer's error can be matched to a single line in the
 * Portaus logs instead of a timestamp range.
 */
function newRequestId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

function refreshTokens(tokenOption, http) {
  return new Promise((resolve, reject) => {
    this.acquireToken(tokenOption)
      .then((accessToken) => {
        http.defaults.headers[tokenOption.name] = accessToken;
        resolve(http.defaults.headers[tokenOption.name]);
      }).catch(reject);
  });
}

class AxiosAuthHttp {
  acquireToken(tokenOption) {
    return new Promise((resolve, reject) => {
      jwt.sign(
        tokenOption.body,
        tokenOption.salt,
        {
          expiresIn: tokenOption.ttl,
        },
        (err, token) => {
          if (err) {
            reject(err);
          }
          resolve(token);
        }
      );
    });
  }

  create(options) {
    if (options == null) {
      throw new Error('Provided `options` for auth-http are null !');
    }

    if (options.token == null) {
      throw new Error('Provided `options.token` for auth-http is null !');
    }

    if (options.token.name == null) {
      throw new Error('A `options.token.name` is required');
    }

    if (options.token.body == null) {
      throw new Error('An `options.token.body` is required');
    }

    if (options.token.salt == null) {
      throw new Error('An `options.token.salt` is required');
    }

    if (options.token.ttl == null) {
      throw new Error('An `options.token.ttl` is required');
    }

    const http = axios.create({
      baseURL: options.baseUrl,
      headers: options.headers,
    });

    http.interceptors.request.use(req => refreshTokens.call(this, options.token, http)
      .then(() => {
        req.headers[options.token.name] = http.defaults.headers[options.token.name];
        var checkIsPaymentApi = `${window.processEnv.VUE_APP_RESET_PASSWORD_API_BASE_URL_V1}/payments/intents`;
        if(checkIsPaymentApi != req.baseURL){
          req.headers[window.processEnv.VUE_APP_AUTH_TOKEN_NAME] = localStorage['access-token'];
        }
        req.headers['X-Request-Id'] = newRequestId();
        return req;
      }), error => Promise.reject(error));

    // Every CMS/CRM/payment call in the app is created through this factory,
    // so this one interceptor covers all backend traffic.
    http.interceptors.response.use(
      (res) => {
        addBreadcrumb('http', `${(res.config.method || '').toUpperCase()} ${describeRequestUrl(res.config)} → ${res.status}`);
        return res;
      },
      (error) => {
        const status = error && error.response ? error.response.status : 'network';
        reportError('API_REQUEST_FAILED', {
          message: `${(error?.config?.method || '').toUpperCase()} ${describeRequestUrl(error?.config)} → ${status}`,
          error,
        });
        return Promise.reject(error);
      }
    );

    return http;
  }
}

export default new AxiosAuthHttp();
